import React from "react";
import { Line } from "react-chartjs-2";
import BillingTrendsChartSkeleton from "./BillingTrendsChartSkeleton";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler, // ✅ for background fill
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler // ✅ register Filler for background color
);

interface BillingCycle {
  billingCycleName: string;
  statuses: {
    Billable: number;
    Billed: number;
    Running: number;
  };
}

interface BillingTrendsChartProps {
  billingCycles: BillingCycle[];
  loading?: boolean;
}

const BillingTrendsChart: React.FC<BillingTrendsChartProps> = ({
  billingCycles,
  loading,
}) => {
  const chartData = React.useMemo(() => {
    const sanitizedCycles = (billingCycles || [])
      .filter((cycle) => cycle?.billingCycleName)
      .map((cycle) => ({
        ...cycle,
        statuses: {
          Billable: cycle.statuses?.Billable || 0,
          Billed: cycle.statuses?.Billed || 0,
          Running: cycle.statuses?.Running || 0,
        },
      }));

    const monthSequence = [
      'Jan', 'Jan-Feb', 'Feb', 'Feb-Mar', 'Mar', 'Mar-Apr',
      'Apr', 'Apr-May', 'May', 'May-Jun', 'Jun', 'Jun-Jul',
      'Jul', 'Jul-Aug', 'Aug', 'Aug-Sep', 'Sep', 'Sep-Oct',
      'Oct', 'Oct-Nov', 'Nov', 'Nov-Dec', 'Dec', 'Dec-Jan'
    ];

    const sortedCycles = [...sanitizedCycles].sort((a, b) => {
      const aName = (a.billingCycleName || '').replace(/^BLC-/, '');
      const bName = (b.billingCycleName || '').replace(/^BLC-/, '');

      const aIndex = monthSequence.indexOf(aName);
      const bIndex = monthSequence.indexOf(bName);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return aName.localeCompare(bName);
    });

    return {
      labels: sortedCycles.map((cycle) =>
        (cycle.billingCycleName || "").replace(/^BLC-/, "")
      ),
      datasets: [
        {
          label: "Billed",
          data: sortedCycles.map((cycle) => cycle.statuses.Billed),
          borderColor: "#0F407A",
          backgroundColor: "#0F407A",
          tension: 0.4,
          fill: false,
          pointRadius: 0,
        },
        {
          label: "Billable",
          data: sortedCycles.map((cycle) => cycle.statuses.Billable),
          borderColor: "#0170AE",
          backgroundColor: "#0170AE",
          tension: 0.4,
          fill: false,
          pointRadius: 0,
        },
        {
          label: "Running",
          data: sortedCycles.map((cycle) => cycle.statuses.Running),
          borderColor: "#A073D8",
          backgroundColor: "#A073D8",
          tension: 0.4,
          fill: false,
          pointRadius: 0,
        },
      ],
    };
  }, [billingCycles]);

  const maxYValue = React.useMemo(() => {
    const allValues = billingCycles.flatMap((cycle) => [
      cycle.statuses.Billed || 0,
      cycle.statuses.Billable || 0,
      cycle.statuses.Running || 0,
    ]);
    const maxY = Math.max(...allValues, 0);
    return Math.ceil(maxY + 2);
  }, [billingCycles]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        max: maxYValue,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          padding: 5,
          stepSize: 1,
          font: {
            size: 10,
          },
          callback: (value: any) => `${value}`,
        },
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.4,
      },
    },
    layout: {
      padding: {
        top: 0,
        right: 5,
        bottom: 20,
        left: 5,
      },
    },
  };

  if (loading) {
    return <BillingTrendsChartSkeleton />;
  }

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h3 style={{ margin: 0, fontWeight: 500, fontSize: "16px" }}>
          Billed & Billable Trend
        </h3>
      </div>
      <div style={{ height: "310px" }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default BillingTrendsChart;
